import { faGlobe } from "@fortawesome/free-solid-svg-icons/faGlobe";
import { faHeadphones } from "@fortawesome/free-solid-svg-icons/faHeadphones";
import { faMinus } from "@fortawesome/free-solid-svg-icons/faMinus";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { faVideo } from "@fortawesome/free-solid-svg-icons/faVideo";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import { useEffect, useState } from "react";
import { Collapse } from "react-bootstrap";
import { CPReactMethods } from "../app";
import { CPImageAlignment } from "../enums/CPImageAlignment";
import { CPLinkType } from "../enums/CPLinkType";
import { CPPartDisplay } from "../models/CPPart";
import { CPProblemDisplay } from "../models/CPProblem";
import ImageDisplayComponent from "./ImageDisplayComponent";
import PartDisplay from "./PartDisplay";
import WarningModalComponent from "./WarningModalComponent";
const cloneDeep = require('lodash.clonedeep')

export interface ProblemDisplayProps {
    problem: CPProblemDisplay
    isPreview: boolean
    isPublic: boolean
    problemIndex: number
    toggleShowProblem?(problemIndex: number): any
    displayProblem: boolean
    displayProblemScore?(problem: CPProblemDisplay): any
    changeProblemInAssignment?(problem: CPProblemDisplay, problemIndex: number): any
    taskPartGuid?: string
    canSubmitLate: boolean
    lateSubmissionPenalty?: number
    latePenaltyDate?: Date
    dueDate?: Date
    isSubmittable: boolean
}

const ProblemDisplay = (props: ProblemDisplayProps) => {
    const [problem, setProblem] = useState(props.problem)
    const [warningContent, setWarningContent] = useState("")

    useEffect(() => {
        if (props.changeProblemInAssignment) {
            props.changeProblemInAssignment(problem, props.problemIndex)
        }
    }, [problem])

    const changeAnswer = (partIndex, value) => {
        if (isAnswerChangeValid(value)) {
            var updatedProblem = cloneDeep(problem)

            if (updatedProblem.parts[partIndex].answers.length == 0) {
                updatedProblem.parts[partIndex].answers.push({ isCorrect: false, isSubmitted: false })
            }
            if (!updatedProblem.parts[partIndex].answers[updatedProblem.parts[partIndex].answers.length - 1].isCorrect || props.isPreview) {
                if (updatedProblem.parts[partIndex].answers[updatedProblem.parts[partIndex].answers.length - 1].isSubmitted) {
                    updatedProblem.parts[partIndex].answers.push({ answer: value, exponentValue: updatedProblem.parts[partIndex].answers[updatedProblem.parts[partIndex].answers.length - 1].exponentValue, isSubmitted: false, isCorrect: false })
                }
                else {
                    updatedProblem.parts[partIndex].answers[updatedProblem.parts[partIndex].answers.length - 1].answer = value
                }
                setProblem(updatedProblem)
            }
            if (props.changeProblemInAssignment) {
                props.changeProblemInAssignment(updatedProblem, props.problemIndex)
            }
        }
    }

    const changeExponent = (partIndex, value) => {
        value = value.trim()
        if (value != "" && value != "-") {
            value = parseInt(value)
        }
        if (value == "" || value == "-" || (!isNaN(value) && isExponentChangeValid(value))) {
            var updatedProblem = cloneDeep(problem)

            if (updatedProblem.parts[partIndex].answers.length == 0) {
                updatedProblem.parts[partIndex].answers.push({ isCorrect: false, isSubmitted: false })
            }
            if (!updatedProblem.parts[partIndex].answers[updatedProblem.parts[partIndex].answers.length - 1].isCorrect || props.isPreview) {
                if (updatedProblem.parts[partIndex].answers[updatedProblem.parts[partIndex].answers.length - 1].isSubmitted) {
                    updatedProblem.parts[partIndex].answers.push({ answer: updatedProblem.parts[partIndex].answers[updatedProblem.parts[partIndex].answers.length - 1].answer, exponentValue: value, isSubmitted: false, isCorrect: false })
                }
                else {
                    updatedProblem.parts[partIndex].answers[updatedProblem.parts[partIndex].answers.length - 1].exponentValue = value
                }
                setProblem(updatedProblem)
            }
            if (props.changeProblemInAssignment) {
                props.changeProblemInAssignment(updatedProblem, props.problemIndex)
            }
        }
    }

    const isAnswerChangeValid = (value: string) => {
        value = value.trim()
        var isNegativeAnswer = value[0] == "-"
        if (isNegativeAnswer) {
            value = value.substring(1)
        }
        if (value.match(/[^.\d]/)) {
            return false
        }
        var numberOfDecimalPoints = (value.match(/[.]/g) || []).length
        if (numberOfDecimalPoints > 1) {
            return false
        }
        if (numberOfDecimalPoints == 0 && value.length > 10) {
            return false
        }
        if (numberOfDecimalPoints == 1) {
            var decimalPointIndex = value.indexOf(".")
            var numbersLeftOfDecimal = value.substring(0, decimalPointIndex).length
            var numbersRightOfDecimal = value.substring(decimalPointIndex + 1).length
            if (numbersLeftOfDecimal > 10) {
                return false
            }
            if (numbersRightOfDecimal > 8) {
                return false
            }
        }
        if (isNegativeAnswer) {
            value = "-" + value
        }
        return true
    }

    const isExponentChangeValid = (value: number) => {
        if (value % 1 != 0) {
            return false
        }
        if (value > 100 || value < -100) {
            return false
        }
        return true
    }

    const isLastAnswerCorrect = (part: CPPartDisplay) => {
        if (part.answers[part.answers.length - 1] != undefined) {
            if (part.answers[part.answers.length - 1].isSubmitted && part.answers[part.answers.length - 1].isCorrect) {
                return true
            }
        }
        return false
    }

    const isAnswerAlreadySubmitted = (part: CPPartDisplay) => {
        if (part.answers.some(attempt => attempt.answer * Math.pow(10, attempt.exponentValue ?? 0) == part.answers[part.answers.length - 1].answer * Math.pow(10, part.answers[part.answers.length - 1].exponentValue ?? 0) && attempt.isSubmitted == true)) {
            return true
        }
        return false
    }

    const isAnswerAlreadySubmittedAndCorrect = (part: CPPartDisplay) => {
        if (part.answers.some(attempt => attempt.answer * Math.pow(10, attempt.exponentValue ?? 0) == part.answers[part.answers.length - 1].answer * Math.pow(10, part.answers[part.answers.length - 1].exponentValue ?? 0) && attempt.isSubmitted && attempt.isCorrect)) {
            return true
        }
        return false
    }

    const isOutOfAttempts = (part: CPPartDisplay) => {
        if (part.maxAttempts == 0) {
            return false
        }
        if (part.maxAttempts! < part.answers.length) {
            return true
        }
        if (part.answers.length > 0 && part.maxAttempts == part.answers.length && part.answers[part.answers.length - 1].isSubmitted) {
            return true
        }
        return false
    }

    const isAnswerEmpty = (part: CPPartDisplay) => {
        if (part.answers.length == 0) {
            return true
        }
        if (CPReactMethods.isNullOrWhiteSpace(part.answers[part.answers.length - 1].answer.toString())) {
            return true
        }
        return false
    }

    const submitAnswer = (part: CPPartDisplay, tempPartIndex: number) => {
        try {
            if (part.answers.length > 0) {
                if (props.isPreview && !props.isPublic) {
                    if (!isAnswerEmpty(part)) {
                        submitAnswerPreview(problem, part, tempPartIndex)
                    }
                }
                else {
                    if (props.isPublic) {
                        if (!isAnswerEmpty(part) && !isLastAnswerCorrect(part)) {
                            submitAnswerPublic(problem, part, tempPartIndex)
                        }
                    }
                    else {
                        if (!isOutOfAttempts(part) && !isAnswerAlreadySubmitted(part) && !isLastAnswerCorrect(part) && !isAnswerEmpty(part)) {
                            submitAnswerStudent(problem, part, tempPartIndex)
                        }
                    }
                }
            }
        }
        catch (ex) {
            console.log(ex)
        }
    }

    const submitAnswerStudent = (problem: CPProblemDisplay, part: CPPartDisplay, tempPartIndex: number) => {
        if (props.isSubmittable) {

            if (!CPReactMethods.isNullOrUndefined(problem.instanceId) && !CPReactMethods.isNullOrUndefined(part.instanceId) && !CPReactMethods.isNullOrUndefined(props.taskPartGuid)) {
                try {
                    var formattedAnswer = formatAnswer(part.answers[part.answers.length - 1].answer.toString())
                    var exponent = part.answers[part.answers.length - 1].exponentValue ??= 0
                    // @ts-ignore
                    window.CPStudent.answerPart(problem.instanceId!, part.instanceId!, props.taskPartGuid!, formattedAnswer, exponent).then(response => {
                        var updatedPart: CPPartDisplay = cloneDeep(part)
                        updatedPart.answers[updatedPart.answers.length - 1].isSubmitted = true
                        updatedPart.answers[updatedPart.answers.length - 1].isCorrect = response.correct;
                        updatedPart.answers[updatedPart.answers.length - 1].answer = formattedAnswer
                        updatedPart.answers[updatedPart.answers.length - 1].answerDate = response.answerDate;
                        var updatedProblem = cloneDeep(problem)
                        updatedProblem.parts[tempPartIndex] = updatedPart
                        setProblem(updatedProblem)
                    }).catch(error => {
                        setWarningContent(error.message)
                        // @ts-ignore
                        var warningModal = new bootstrap.Modal(document.getElementById("problemWarningModal" + props.problemIndex), { backdrop: true, keyboard: true, focus: true })
                        warningModal.show();
                    })
                }
                catch (error) {
                    var message: string = "" + error
                    setWarningContent(message)
                    // @ts-ignore
                    var warningModal = new bootstrap.Modal(document.getElementById("problemWarningModal" + props.problemIndex), { backdrop: true, keyboard: true, focus: true })
                    warningModal.show();
                }
            }
            else {
                // Shouldn't need an else because there won't be any standalone problems
            }
        } else {
            // Due Date past, can't submit
            /*          window.GlobalMethods.ShowToast({ unsubmittable: new Toast() {
                      }
          })*/
            alert("Cannot submit, assignment over.");
        }
    }

    const submitAnswerPublic = (problem: CPProblemDisplay, part: CPPartDisplay, tempPartIndex: number) => {
        if (!CPReactMethods.isNullOrUndefined(problem.instanceId) && !CPReactMethods.isNullOrUndefined(part.instanceId)) {
            try {
                var formattedAnswer = formatAnswer(part.answers[part.answers.length - 1].answer.toString())
                var exponent = part.answers[part.answers.length - 1].exponentValue ??= 0
                window.CPMethods.answerPart(problem.instanceId!, part.instanceId!, problem.publicSeed!, formattedAnswer, exponent).then(response => {
                    var updatedPart: CPPartDisplay = cloneDeep(part)
                    updatedPart.answers[updatedPart.answers.length - 1].isSubmitted = true
                    updatedPart.answers[updatedPart.answers.length - 1].isCorrect = response
                    updatedPart.answers[updatedPart.answers.length - 1].answer = formattedAnswer
                    var updatedProblem = cloneDeep(problem)
                    updatedProblem.parts[tempPartIndex] = updatedPart
                    setProblem(updatedProblem)
                }).catch(error => {
                    setWarningContent(error.message)
                    // @ts-ignore
                    var warningModal = new bootstrap.Modal(document.getElementById("problemWarningModal" + props.problemIndex), { backdrop: true, keyboard: true, focus: true })
                    warningModal.show();
                })
            }
            catch (error) {
                var message: string = "" + error
                setWarningContent(message)
                // @ts-ignore
                var warningModal = new bootstrap.Modal(document.getElementById("problemWarningModal" + props.problemIndex), { backdrop: true, keyboard: true, focus: true })
                warningModal.show();
            }
        } else {
            // Standalone
            try {
                var formattedAnswer = formatAnswer(part.answers[part.answers.length - 1].answer.toString())
                var exponent = part.answers[part.answers.length - 1].exponentValue ??= 0
                window.CPMethods.answerPartStandalone(problem.id, part.id!, problem.publicSeed!, formattedAnswer, exponent).then(response => {
                    var updatedPart: CPPartDisplay = cloneDeep(part)
                    updatedPart.answers[updatedPart.answers.length - 1].isSubmitted = true
                    updatedPart.answers[updatedPart.answers.length - 1].isCorrect = response.correct
                    updatedPart.answers[updatedPart.answers.length - 1].answer = formattedAnswer
                    var updatedProblem = cloneDeep(problem)
                    updatedProblem.parts[tempPartIndex] = updatedPart
                    setProblem(updatedProblem)
                }).catch(error => {
                    setWarningContent(error.message)
                    // @ts-ignore
                    var warningModal = new bootstrap.Modal(document.getElementById("problemWarningModal" + props.problemIndex), { backdrop: true, keyboard: true, focus: true })
                    warningModal.show();
                })
            }
            catch (error) {
                var message: string = "" + error
                setWarningContent(message)
                // @ts-ignore
                var warningModal = new bootstrap.Modal(document.getElementById("problemWarningModal" + props.problemIndex), { backdrop: true, keyboard: true, focus: true })
                warningModal.show();
            }
        }
    }

    const submitAnswerPreview = (problem: CPProblemDisplay, part: CPPartDisplay, tempPartIndex: number) => {
        if (!CPReactMethods.isNullOrUndefined(problem.instanceId) && !CPReactMethods.isNullOrUndefined(part.instanceId)) {
            try {
                var formattedAnswer = formatAnswer(part.answers[part.answers.length - 1].answer.toString())
                var exponent = part.answers[part.answers.length - 1].exponentValue ??= 0
                window.CPInstructor.answerPartPreview(problem.instanceId!, part.instanceId!, problem.publicSeed!, formattedAnswer, exponent).then(response => {
                    var updatedPart: CPPartDisplay = cloneDeep(part)
                    updatedPart.answers[updatedPart.answers.length - 1].isSubmitted = true
                    updatedPart.answers[updatedPart.answers.length - 1].isCorrect = response.preview.correct
                    updatedPart.answers[updatedPart.answers.length - 1].answer = formattedAnswer
                    updatedPart.value = response.preview.value
                    updatedPart.min = response.preview.min
                    updatedPart.max = response.preview.max
                    var updatedProblem = cloneDeep(problem)
                    updatedProblem.parts[tempPartIndex] = updatedPart
                    setProblem(updatedProblem)
                }).catch(error => {
                    setWarningContent(error.message)
                    // @ts-ignore
                    var warningModal = new bootstrap.Modal(document.getElementById("problemWarningModal" + props.problemIndex), { backdrop: true, keyboard: true, focus: true })
                    warningModal.show();
                })
            }
            catch (error) {
                var message: string = "" + error
                setWarningContent(message)
                // @ts-ignore
                var warningModal = new bootstrap.Modal(document.getElementById("problemWarningModal" + props.problemIndex), { backdrop: true, keyboard: true, focus: true })
                warningModal.show();
            }
        }
        else {
            // Standalone
            try {
                var formattedAnswer = formatAnswer(part.answers[part.answers.length - 1].answer.toString())
                var exponent = part.answers[part.answers.length - 1].exponentValue ??= 0
                window.CPInstructor.answerPartPreviewStandalone(problem.id, part.id!, problem.publicSeed!, formattedAnswer, exponent).then(response => {
                    var updatedPart: CPPartDisplay = cloneDeep(part)
                    updatedPart.answers[updatedPart.answers.length - 1].isSubmitted = true
                    updatedPart.answers[updatedPart.answers.length - 1].isCorrect = response.preview.correct
                    updatedPart.answers[updatedPart.answers.length - 1].answer = formattedAnswer
                    updatedPart.value = response.preview.value
                    updatedPart.min = response.preview.min
                    updatedPart.max = response.preview.max
                    var updatedProblem = cloneDeep(problem)
                    updatedProblem.parts[tempPartIndex] = updatedPart
                    setProblem(updatedProblem)
                }).catch(error => {
                    setWarningContent(error.message)
                    // @ts-ignore
                    var warningModal = new bootstrap.Modal(document.getElementById("problemWarningModal" + props.problemIndex), { backdrop: true, keyboard: true, focus: true })
                    warningModal.show();
                })
            }
            catch (error) {
                var message: string = "" + error
                setWarningContent(message)
                // @ts-ignore
                var warningModal = new bootstrap.Modal(document.getElementById("problemWarningModal" + props.problemIndex), { backdrop: true, keyboard: true, focus: true })
                warningModal.show();
            }
        }
    }

    const formatAnswer = (answer: string) => {
        var formattedAnswer = parseFloat(answer)
        if (isNaN(formattedAnswer)) {
            throw answer + " is not a number."
        }
        return formattedAnswer
    }

    return (
        <div className="card my-3" id={"problem" + (props.problemIndex + 1)}>
            <div className="card-header" role={props.toggleShowProblem ? "button" : ""} aria-controls={"#collapse" + (props.problemIndex + 1)} onClick={() => props.toggleShowProblem ? props.toggleShowProblem(props.problemIndex) : {}}>
                <div className="row">
                    <div className="col">
                        {props.toggleShowProblem ? <span className="fw-bold fs-4" id={"collapseIcon" + props.problemIndex}>{props.displayProblem ? <FontAwesomeIcon icon={faMinus} /> : <FontAwesomeIcon icon={faPlus} />}</span> : ""}
                        <b>&emsp;{(props.problemIndex + 1) + ". " + problem.name}</b>
                    </div>
                    {props.displayProblemScore ?
                        <div className="col-auto">
                            Points: {props.displayProblemScore(problem)}
                        </div>
                        : ""}
                </div>
            </div>
            <Collapse in={props.displayProblem} >
                <div className="card-body">
                    <div className="row">
                        <ImageDisplayComponent
                            image={problem.image}
                            alignment={CPImageAlignment.Left}
                            isForMobile={false}
                        />
                        <ImageDisplayComponent
                            image={problem.image}
                            alignment={CPImageAlignment.Above}
                            isForMobile={false}
                        />
                        <div className="col card-title">
                            <div dangerouslySetInnerHTML={{ __html: problem.text! }}></div>
                        </div>
                        <ImageDisplayComponent
                            image={problem.image}
                            alignment={CPImageAlignment.Right}
                            isForMobile={false}
                        />
                    </div>
                    <ImageDisplayComponent
                        image={problem.image}
                        alignment={CPImageAlignment.Middle}
                        isForMobile={false}
                    />
                    <ol className="partList">
                        {problem.parts.map((part, partIndex) => {
                            return (
                                <PartDisplay key={part.id}
                                    part={part}
                                    isPreview={props.isPreview}
                                    isPublic={props.isPublic}
                                    submitAnswer={submitAnswer}
                                    problemIndex={props.problemIndex}
                                    partIndex={partIndex}
                                    changeAnswer={changeAnswer}
                                    changeExponent={changeExponent}
                                    isLastAnswerCorrect={isLastAnswerCorrect}
                                    isOutOfAttempts={isOutOfAttempts}
                                    isAnswerAlreadySubmitted={isAnswerAlreadySubmitted}
                                    isAnswerAlreadySubmittedAndCorrect={isAnswerAlreadySubmittedAndCorrect}
                                    isOnePartProblem={problem.parts.length == 1}
                                    canSubmitLate={props.canSubmitLate}
                                    lateSubmissionPenalty={props.lateSubmissionPenalty}
                                    latePenaltyDate={props.latePenaltyDate}
                                    isSubmittable={props.isSubmittable}
                                />
                            )
                        })}
                    </ol>
                    <ImageDisplayComponent
                        image={problem.image}
                        alignment={CPImageAlignment.Below}
                        isForMobile={false}
                    />
                    {problem.helpText ?
                        <div className="row">
                            <div className="col">
                                <div className="row">
                                    <b>Help Text</b>
                                </div>
                                <div className="row">
                                    <div className="col" dangerouslySetInnerHTML={{ __html: problem.helpText }}></div>
                                </div>
                            </div>
                        </div> : ""
                    }
                    {problem.links ?
                        problem.links.length > 0 ?
                            <div className="row">
                                <div className="col">
                                    <div className="row">
                                        <b>Help Links</b>
                                    </div><div className="row">
                                        <span>
                                            {problem.links.map(link => {
                                                return (
                                                    <a key={link.url} href={link.url} target="_blank"><FontAwesomeIcon className="mx-2" icon={link.type == CPLinkType.Video ? faVideo : link.type == CPLinkType.Audio ? faHeadphones : faGlobe} size="lg" /></a>
                                                )
                                            })}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            : "" : ""}
                </div>
            </Collapse>
            <WarningModalComponent
                itemId={"problemWarningModal" + props.problemIndex}
                itemContent={warningContent}
            />
        </div>
    )
}

export default ProblemDisplay